import { COMPONENT_VALUE_TYPES as types } from 'shared/constants';
import type { PropsSchemaList } from 'shared/types/PropsSchema';

export interface Props {
  heroImage?: {
    url?: string;
    alt?: string;
  };
  title?: string;
  subtitle?: string;
  cardHeader?: string;
  cardDesc?: string;
  cardList?: {
    image?: {
      url?: string;
      alt?: string;
    };
    title?: string;
    subtitle?: string;
    comingSoonEnabled?: boolean;
    comingSoonText?: string;
  }[];
  fpoSection?: {
    secondHeader?: string;
    secondDesc?: string;
    title1?: string;
    descList1?: {
      desc?: string;
    }[];
    title2?: string;
    descList2?: {
      desc?: string;
    }[];
    applyCta?: {
      label?: string;
      url?: string;
    };
    learnMoreCta?: {
      label?: string;
      url?: string;
    };
  }
  labSection?: {
    image?: {
      url?: string;
      alt?: string;
    };
    title?: string;
    desc?: string;
    cta?: {
      label?: string;
      url?: string;
    };
  };
}

const propsSchema: PropsSchemaList = [
  {
    key: 'heroImage',
    label: 'Hero Image',
    type: types.OBJECT,
    children: [
      {
        key: 'url',
        label: 'URL',
        type: types.S3_ASSET_URL,
      },
      {
        key: 'alt',
        label: 'Image Alternative Text (A brief description of the image to optimize search engine visibility)',
        type: types.TEXT,
      },
    ]
  },
  {
    key: 'title',
    label: 'Title',
    type: types.TEXT,
  },
  {
    key: 'subtitle',
    label: 'Subtitle',
    type: types.TEXT,
  },
  {
    key: 'cardHeader',
    label: 'Card Header',
    type: types.TEXT,
  },
  {
    key: 'cardDesc',
    label: 'Card Description',
    type: types.TEXT,
  },
  {
    key: 'cardList',
    label: 'Card List',
    type: types.ARRAY,
    children: [
      {
        key: 'image',
        label: 'Image',
        type: types.OBJECT,
        children: [
          {
            key: 'url',
            label: 'URL',
            type: types.S3_ASSET_URL,
          },
          {
            key: 'alt',
            label: 'Image Alternative Text (A brief description of the image to optimize search engine visibility)',
            type: types.TEXT,
          },
        ]
      },
      {
        key: 'title',
        label: 'Title',
        type: types.TEXT,
      },
      {
        key: 'subtitle',
        label: 'Subtitle',
        type: types.TEXT,
      },
      {
        key: 'comingSoonEnabled',
        label: 'Coming Soon Enabled',
        type: types.BOOLEAN,
      },
      {
        key: 'comingSoonText',
        label: 'Coming Soon Text',
        type: types.TEXT,
      },
    ]
  },
  {
    key: 'fpoSection',
    label: 'FPO Section',
    type: types.OBJECT,
    children: [
      {
        key: 'secondHeader',
        label: 'Second Header',
        type: types.TEXT,
      },
      {
        key: 'secondDesc',
        label: 'Second Description',
        type: types.TEXT,
      },
      {
        key: 'title1',
        label: 'Title 1',
        type: types.TEXT,
      },
      {
        key: 'descList1',
        label: 'Desc List 1',
        type: types.ARRAY,
        children: [
          {
            key: 'desc',
            label: 'Description',
            type: types.TEXT,
          }
        ]
      },
      {
        key: 'title2',
        label: 'Title 2',
        type: types.TEXT,
      },
      {
        key: 'descList2',
        label: 'Desc List 2',
        type: types.ARRAY,
        children: [
          {
            key: 'desc',
            label: 'Description',
            type: types.TEXT,
          }
        ]
      },
      {
        key: 'applyCta',
        label: 'Apply CTA',
        type: types.OBJECT,
        children: [
          {
            key: 'label',
            label: 'Label',
            type: types.TEXT,
          },
          {
            key: 'url',
            label: 'URL',
            type: types.TEXT,
          },
        ]
      },
      {
        key: 'learnMoreCta',
        label: 'Learn More CTA',
        type: types.OBJECT,
        children: [
          {
            key: 'label',
            label: 'Label',
            type: types.TEXT,
          },
          {
            key: 'url',
            label: 'URL',
            type: types.TEXT,
          },
        ]
      },
    ]
  },
  {
    key: 'labSection',
    label: 'Lab Section',
    type: types.OBJECT,
    children: [
      {
        key: 'image',
        label: 'Image',
        type: types.OBJECT,
        children: [
          {
            key: 'url',
            label: 'URL',
            type: types.S3_ASSET_URL,
          },
          {
            key: 'alt',
            label: 'Image Alternative Text (A brief description of the image to optimize search engine visibility)',
            type: types.TEXT,
          },
        ]
      },
      {
        key: 'title',
        label: 'Title',
        type: types.TEXT,
      },
      {
        key: 'desc',
        label: 'Description',
        type: types.TEXT,
      },
      {
        key: 'cta',
        label: 'CTA',
        type: types.OBJECT,
        children: [
          {
            key: 'label',
            label: 'Label',
            type: types.TEXT,
          },
          {
            key: 'url',
            label: 'URL',
            type: types.TEXT,
          },
        ]
      },
    ]
  },
];

const defaultProps: Props = {
  heroImage: {
    url: '',
    alt: '',
  },
  title: 'To co-create together on the six platforms',
  subtitle: 'Building an Exciting AI Mobility Ecosystem with Our Technology Co-Creators',
  cardHeader: 'FF Co-Creation Open Platform',
  cardDesc: `The FF Co-Creation Open Platform is FF's official co-creation hub, especially for the participation of Technology Co-Creators (FTOs) in all open projects. 
  The platform consists of six major components: the Application Open Platform, OS Open Platform, Mechanical Structure Open Platform, Electronic Hardware Open Platform, 
  Cloud Open Platform, and AI Engine Open Platform. FTOs can choose suitable entry points to participate based on their interests and expertise. 
  Leveraging FF's cutting-edge aiHyper 6x4 technology architecture, FTOs will collaborate with FF to build an exciting AI mobility ecosystem.`,
  cardList: [
    {
      image: {
        url: 'https://genesis-cdn.ff.com/test/four-squares.svg',
        alt: '',
      },
      title: 'FF OpenApp',
      subtitle: `With full advantage of FF Open Ecosystem, Publish your application with the FF Co-Creation Open Platform, 
      will enable your app to serve FF 91, future FF models, 
      and potentially opening up to all smart vehicles in the industry in the near future.`,
    },
    {
      image: {
        url: 'https://genesis-cdn.ff.com/test/four-squares.svg',
        alt: '',
      },
      title: 'FF aiOS 2',
      subtitle: `You will be involved in the development of FF's brand new FF aiOS 2, contributing to the soul and the most essential technology of FF 91.`,
      comingSoonEnabled: true,
      comingSoonText: '(Coming soon)',
    },
    {
      image: {
        url: 'https://genesis-cdn.ff.com/test/four-squares.svg',
        alt: '',
      },
      title: 'FF aiHW 2.0',
      subtitle: `You will participate in FF's crucial electronic component development including Computing, V-Network, Communication, peripherals, etc.`,
      comingSoonEnabled: true,
      comingSoonText: '(Coming soon)',
    },
    {
      image: {
        url: 'https://genesis-cdn.ff.com/test/four-squares.svg',
        alt: '',
      },
      title: 'FF Mechanical',
      subtitle: `You will be involved in the development and improvement of all mechanical parts, including Power train, chasis, Energy Exchanging, Safety, Ergonomics at FF.`,
      comingSoonEnabled: true,
      comingSoonText: '(Coming soon)',
    },
    {
      image: {
        url: 'https://genesis-cdn.ff.com/test/four-squares.svg',
        alt: '',
      },
      title: 'FF Cloud',
      subtitle: `You will be involved in the refinement and improvement of Cloud related functions, like toolchain, data mining, AI training, Web 3.0, etc`,
    },
    {
      image: {
        url: 'https://genesis-cdn.ff.com/test/four-squares.svg',
        alt: '',
      },
      title: 'FF AI',
      subtitle: `You will build your custom model based on state-of-the-art AI algorithms and pre-trained models which prepared by FTOs. And co-create models and prompt ecosystems with all FTOs.`,
    },
  ],
  fpoSection: {
    secondHeader: `Apply to become FTO, Join to Co-Creation Open Platform`,
    secondDesc: `(Futurist Technology Officer): FTO participates in the development of in-vehicle software, AI algorithms, applications and services through the SDK, 
    technology open platform and application release platform released by FF. 
    FTO can also work with FF engineers through the open-source community to provide valuable R&D support to FF and jointly drive technology transformations in the automotive industry.`,
    title1: 'Qualifications:',
    descList1: [
      {
        desc: `At least 5 years of experience in mobility software design or development;`,
      },
      {
        desc: `Technical background on the Internet, AI product development, and big data;`,
      },
      {
        desc: `Experience in at least two out of the six major platforms (applications/software/hardware/mechanics/cloud computing/operating systems);`,
      },
      {
        desc: `Keen insights into technology trends in the mobility market;`,
      },
      {
        desc: `An innovative technical mindset with hands-on execution and strong interest in AI;`,
      },
      {
        desc: `Ability to allocate enough time to participate in co-creation technology discussions and R&D;`,
      },
      {
        desc: `Comfortable in using FF products as one of the main means of daily transportation`,
      },
    ],
    title2: 'Preferred experience:',
    descList2: [
      {
        desc: `Successfully led at least 1 technology research and development project within the past year and has made effective technological improvements;`,
      },
      {
        desc: `Independent developer experience with successful project cases is preferred;`,
      },
      {
        desc: `Industry award winners preferred.`,
      }
    ],
    applyCta: {
      label: 'Apply to Join ',
      url: 'https://www.ff.com/us/fto',
    },
    learnMoreCta: {
      label: 'Learn More',
      url: 'https://www.ff.com/us/fto',
    },
  }
  ,
  labSection: {
    image: {
      url: 'https://genesis-cdn.ff.com/test/four-squares.svg',
      alt: '',
    },
    title: `Try FF's latest experiments in Labs`,
    desc: `Collaborate in pioneering experiments to co-create the next wave of technological advancements.`,
    cta: {
      label: 'Learn More',
      url: 'https://www.ff.com',
    },
  },
};

export default {
  title: 'Hero Image Grid',
  id: 'HeroImageGrid',
  propsSchema,
  defaultProps,
}
